import { useCallback, useEffect, useRef, useState } from "react";

import { api_without_cancellation } from "../../../../api/api";

const CLIENT_SEARCH_DEBOUNCE_MS = 400;

const useGetClientFilters = (searchQuery = "") => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  const fetchClients = useCallback(async (query) => {
    const normalizedQuery = typeof query === "string" ? query.trim() : "";
    const requestId = ++requestIdRef.current;

    if (!normalizedQuery) {
      setData([]);
      setError(null);
      setLoading(false);
      return [];
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api_without_cancellation.get("/api/firmsetting-page/client-filter/", {
        params: { search: normalizedQuery },
      });

      if (requestId === requestIdRef.current) {
        setData(Array.isArray(response.data) ? response.data : []);
      }

      return response.data;
    } catch (error) {
      if (requestId === requestIdRef.current) {
        setData([]);
        setError(error);
      }

      return [];
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const normalizedQuery = searchQuery.trim();

    requestIdRef.current += 1;
    setData([]);
    setError(null);

    if (!normalizedQuery) {
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const debounceTimer = window.setTimeout(() => {
      fetchClients(normalizedQuery);
    }, CLIENT_SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(debounceTimer);
      requestIdRef.current += 1;
    };
  }, [fetchClients, searchQuery]);

  const refetch = useCallback(() => fetchClients(searchQuery), [fetchClients, searchQuery]);

  return { data, loading, error, refetch };
};

export default useGetClientFilters;

const useSaveClients = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const clientMerge = async (payload) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await api_without_cancellation.post(
        "/api/firmsetting-page/client-merge/",
        payload
      );
      setSuccess(response.data);
      return response.data;
    } catch (error) {
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { clientMerge, loading, error, success };
};

export { useSaveClients };
